import React from "react";
import { MapPin, CalendarClock, CheckCircle2, ExternalLink, ShieldCheck, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const DEADLINE_STYLE = {
  "closing-soon": "bg-red-50 text-red-700 border-red-200",
  rolling: "bg-emerald-50 text-emerald-700 border-emerald-200",
  varies: "bg-amber-50 text-amber-700 border-amber-200",
  none: "bg-slate-100 text-slate-600 border-slate-200",
};

export const ResourceCard = ({ r, categoryName }) => {
  const foundation = r.owner === "foundation";
  const isInternal = r.application_link?.startsWith("/");

  return (
    <div data-testid={`resource-card-${r.id}`}
      className="card-hover bg-white rounded-2xl border border-slate-200 p-6 flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-4">
        <Badge className={`rounded-full font-medium border ${foundation ? "bg-navy-900 text-white border-navy-900" : "bg-slate-100 text-navy-800 border-slate-200"}`}>
          {foundation ? <ShieldCheck className="h-3 w-3 mr-1" /> : <Building2 className="h-3 w-3 mr-1" />}
          {foundation ? "Foundation Program" : "Verified Third-Party"}
        </Badge>
        {r.featured && <span className="text-[10px] font-bold uppercase tracking-wider text-gold">Featured</span>}
      </div>

      {categoryName && <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-gold mb-2">{categoryName}</span>}
      <h3 className="font-display text-xl font-bold text-navy-900 leading-snug">{r.program}</h3>
      <p className="text-sm text-slate-500 font-medium mt-1 mb-3">{r.organization}</p>
      <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">{r.short_description}</p>

      <div className="space-y-2 text-sm text-slate-600 border-t border-slate-100 pt-4">
        <p className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-verified mt-0.5 shrink-0" /><span><span className="font-semibold text-navy-800">Eligibility:</span> {r.eligibility}</span></p>
        <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-navy-700 shrink-0" /><span className="font-semibold text-navy-800">{r.geography}</span></p>
        <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-navy-700 shrink-0" />
          <Badge variant="outline" className={`rounded-full text-xs ${DEADLINE_STYLE[r.deadline_type] || DEADLINE_STYLE.none}`}>
            {r.deadline || "No deadline"}
          </Badge>
        </div>
      </div>

      <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
        <span className="text-[11px] text-slate-400">Verified {r.last_verified}</span>
        {isInternal ? (
          <a href={r.application_link} data-testid="opportunity-card-apply-btn">
            <Button size="sm" className="rounded-full bg-navy-900 hover:bg-navy-700 text-white">Apply</Button>
          </a>
        ) : (
          <a href={r.application_link} target="_blank" rel="noopener noreferrer" data-testid="opportunity-card-apply-btn">
            <Button size="sm" className="rounded-full bg-navy-900 hover:bg-navy-700 text-white">
              Visit Program <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </a>
        )}
      </div>
    </div>
  );
};

export const SectionHeading = ({ overline, title, subtitle, light, center }) => (
  <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} mb-10`}>
    {overline && <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">{overline}</span>}
    <h2 className={`font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 ${light ? "text-white" : "text-navy-900"}`}>{title}</h2>
    {subtitle && <p className={`mt-4 text-base leading-relaxed ${light ? "text-slate-300" : "text-slate-600"}`}>{subtitle}</p>}
  </div>
);
